/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { 
  Wrench, Shield, Zap, Flame, Compass, Play, 
  Dribbble, Volume2, VolumeX, ListCollapse, Award
} from 'lucide-react';
import { GameDifficulty } from '../types';

interface MainMenuProps {
  difficulty: GameDifficulty;
  setDifficulty: (dif: GameDifficulty) => void;
  onStartGame: () => void;
  bestScore: number;
  isMuted: boolean;
  toggleMute: () => void;
}

export const MainMenu: React.FC<MainMenuProps> = ({
  difficulty,
  setDifficulty,
  onStartGame,
  bestScore,
  isMuted,
  toggleMute,
}) => {
  return (
    <div id="main-menu" className="w-full max-w-4xl mx-auto bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl flex flex-col md:flex-row min-h-[500px]">
      
      {/* LEFT COLUMN: HERO AREA & DIFFICULTY SELECTOR */}
      <div className="flex-1 p-8 flex flex-col justify-between bg-gradient-to-br from-slate-900 to-slate-950 border-r border-slate-900/80">
        
        {/* Title */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-cyan-500 font-mono text-[11px] font-bold tracking-widest uppercase">
              // ARCADE FIGHTER COCKPIT
            </span>
            <button 
              id="menu-mute-btn"
              onClick={toggleMute}
              className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-950 transition cursor-pointer"
            >
              {isMuted ? <VolumeX className="w-4 h-4 text-rose-500" /> : <Volume2 className="w-4 h-4 text-emerald-400" />}
            </button>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-none">
            AIR STRIKE
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-300 font-black">
              TRAINING
            </span>
          </h1>
          <p className="text-xs text-slate-400 font-mono tracking-wide">
            2D AUTOMATIC TARGET-LOCK FLIGHT COMBAT SIMULATOR v1.4.0
          </p>
        </div>

        {/* Difficulty Selection Cards */}
        <div className="space-y-3 my-6">
          <span className="block text-xs font-mono text-slate-500 tracking-wider">
            ENGAGEMENT LEVEL:
          </span>
          <div className="grid grid-cols-3 gap-2.5">
            {[
              { id: 'rookie', label: 'ROOKIE', desc: '1.0x HP. Slow Drone Speeds', color: 'border-emerald-800/40 text-emerald-400 hover:bg-emerald-950/20' },
              { id: 'veteran', label: 'VETERAN', desc: '1.4x HP. Active Target Lock', color: 'border-amber-800/40 text-amber-400 hover:bg-amber-950/20' },
              { id: 'ace', label: 'ACE COCKPIT', desc: '1.8x HP. Bullet Barrages', color: 'border-red-800/40 text-rose-400 hover:bg-rose-950/20' }
            ].map(lvl => (
              <button
                key={lvl.id}
                id={`diff-btn-${lvl.id}`}
                onClick={() => setDifficulty(lvl.id as GameDifficulty)}
                className={`p-3 rounded-xl border text-center transition cursor-pointer relative overflow-hidden ${
                  difficulty === lvl.id 
                    ? 'border-cyan-500 bg-cyan-950/30 text-white shadow-[0_0_12px_rgba(6,182,212,0.15)] font-bold' 
                    : 'bg-slate-900/60 border-slate-800 text-slate-400'
                }`}
              >
                <div className="text-xs tracking-wider font-mono">{lvl.label}</div>
                {difficulty === lvl.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-cyan-400" />
                )}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 italic mt-1 font-mono text-center">
            {difficulty === 'rookie' && "✓ Excellent path to practice controls. Re-charge shields quickly."}
            {difficulty === 'veteran' && "✓ Hostile helicopters and tracking missile launchers active."}
            {difficulty === 'ace' && "⚠ Extreme threat envelope. Heavy bullet fire and aggressive AI."}
          </div>
        </div>

        {/* Bottom Engage Button */}
        <button
          id="engage-training-btn"
          onClick={onStartGame}
          className="w-full py-4 rounded-xl font-bold tracking-widest text-black bg-gradient-to-r from-cyan-400 via-sky-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_4px_20px_rgba(6,182,212,0.35)] flex items-center justify-center gap-2 cursor-pointer text-sm"
        >
          <Play className="w-5 h-5 fill-black" />
          LAUNCH COMBAT RUN
        </button>

        {/* High Score indicators */}
        <div className="mt-4 pt-4 border-t border-slate-900/40 flex items-center justify-between text-xs font-mono text-slate-500 bg-slate-900/10 px-1 py-1 rounded">
          <span className="flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-yellow-500" /> PERSONAL BEST</span>
          <span className="text-yellow-400 font-black tracking-wider text-sm">{bestScore.toLocaleString()} PTS</span>
        </div>
      </div>

      {/* RIGHT COLUMN: MANUALS, CONTROLS AND POWER-UPS */}
      <div className="flex-1 p-8 bg-slate-950/40 space-y-6 overflow-y-auto">
        
        {/* Section 1: Keyboard Mapping controls */}
        <div className="space-y-3">
          <h3 className="text-xs font-mono text-cyan-400 tracking-wider flex items-center gap-1.5 border-b border-slate-800 pb-2">
            <Compass className="w-3.5 h-3.5" /> STEER CONFIG
          </h3>
          <div className="grid grid-cols-2 gap-3.5 text-xs">
            <div className="flex items-center justify-between bg-slate-900/50 p-2 rounded-lg border border-slate-800">
              <span className="text-slate-400">Flight Moves</span>
              <kbd className="px-1.5 py-0.5 rounded text-[10px] bg-slate-800 border border-slate-700 text-white font-bold">W / A / S / D</kbd>
            </div>
            <div className="flex items-center justify-between bg-slate-900/50 p-2 rounded-lg border border-slate-800">
              <span className="text-slate-400">Glide Steering</span>
              <kbd className="px-1.5 py-0.5 rounded text-[10px] bg-slate-800 border border-slate-700 text-white font-bold">↑ ↓ ← →</kbd>
            </div>
            <div className="flex items-center justify-between bg-slate-900/50 p-2 rounded-lg border border-slate-800">
              <span className="text-slate-400">Special Missile</span>
              <kbd className="px-1.5 py-0.5 rounded text-[10px] bg-slate-800 border border-slate-700 text-white font-bold">SPACE</kbd>
            </div>
            <div className="flex items-center justify-between bg-slate-900/50 p-2 rounded-lg border border-slate-800">
              <span className="text-slate-400">Speed Boost</span>
              <kbd className="px-1.5 py-0.5 rounded text-[10px] bg-slate-800 border border-slate-700 text-white font-bold">L_SHIFT</kbd>
            </div>
          </div>
        </div>

        {/* Section 2: Collective Power-ups overview */}
        <div className="space-y-3">
          <h3 className="text-xs font-mono text-cyan-400 tracking-wider flex items-center gap-1.5 border-b border-slate-800 pb-2">
            <Flame className="w-3.5 h-3.5" /> COLLECTIBLE BATTLE POWER-UPS
          </h3>
          <div className="space-y-2.5">
            {[
              { label: 'HULL RECON (♥)', desc: 'Repairs jet stability by 35%', style: 'text-rose-400 bg-rose-950/20 border-rose-900/35' },
              { label: 'BARRIER INTEGRITY (S)', desc: 'Instantly shields shield generator fully', style: 'text-cyan-400 bg-cyan-950/20 border-cyan-900/35' },
              { label: 'SEEKER MATRIX (T)', desc: 'Activates homing multi-tipped launcher rockets', style: 'text-amber-400 bg-amber-950/20 border-amber-900/35' },
              { label: 'RAPID MATRIX (R)', desc: 'Overclocks primary automatic lasers fire rate 2x', style: 'text-orange-400 bg-orange-950/20 border-orange-900/35' },
              { label: 'EMP BLAST (E)', desc: 'Blocks visible missile projectiles & shocks screen units', style: 'text-purple-400 bg-purple-950/20 border-purple-900/35' },
              { label: 'INVINCIBILITY (I)', desc: 'Renders fighter structure immune to crashes', style: 'text-teal-400 bg-teal-950/20 border-teal-900/35' },
            ].map((pu, idx) => (
              <div 
                key={idx} 
                className={`p-2 rounded-xl border flex items-center gap-3 text-xs ${pu.style}`}
              >
                <div className="font-bold whitespace-nowrap">{pu.label}</div>
                <div className="text-[11px] text-slate-300 ml-auto text-right">{pu.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Target indication auto text note */}
        <div className="p-3 bg-cyan-950/20 border border-cyan-900/40 text-[11px] text-cyan-300 rounded-xl space-y-1 font-mono">
          <div className="font-bold flex items-center gap-1">
            <Shield className="w-3.5 h-3.5 text-cyan-400" /> AUTOMATIC PILOT SYSTEM
          </div>
          <p className="leading-normal">
            Primary twin lasers will automatically trigger and adjust focus sweeps sweep on nearby threats. Keep flight lines clear from physical objects, collisions and missile trails!
          </p>
        </div>

      </div>

    </div>
  );
};
